import { type SetStateAction, useCallback, useRef, useState } from "react"

import { useQuery } from "@tanstack/react-query"
import toast from "react-hot-toast"
import { useTranslation } from "react-i18next"
import { Link, Navigate, useLocation, useParams } from "react-router-dom"
import { Button, Comment, Header, Icon, Loader, Message, Pagination } from "semantic-ui-react"
import { RichTextViewer } from "simplydo/react/richtext"

import api, { asQuery } from "api"
import { useAppSelector } from "store"
import util from "utils/utils"

import { CommentInput } from "./CommentInput"
import DisscussionComment from "./DiscussionComment"

export type CommentState = {
  [key: string]: {
    message: string
    media: Array<{
      value: string
      name: string
      signedDownloadRequest?: string
      type: "image" | "file" | "gif"
      data?: any
    }>
    loading?: boolean
  }
}

export const DiscussionBoard = () => {
  const { t } = useTranslation()
  const discussionId = useParams<{ discussionId: string }>().discussionId
  const [params, setParams] = useState({ page: 1 })
  const inputRef = useRef()
  const location = useLocation()

  const [commentState, setCommentState] = useState<CommentState>({
    new: {
      message: "",
      media: [],
      loading: false,
    },
  })

  const discussionCommentQuery = useQuery({
    queryKey: ["discussions", discussionId, "comments", params],
    queryFn: asQuery(api.discussions.getComments, {
      params: [discussionId, params],
    }),
    enabled: !!discussionId,
  })

  const forType = discussionCommentQuery.data?.discussion?.forType
  const forId = discussionCommentQuery.data?.discussion?.forId

  const groupQuery = useQuery({
    queryKey: ["group", forId],
    queryFn: asQuery(api.groups.get, {
      params: [forId],
    }),
    enabled: forType === "group",
  })

  const user = useAppSelector((state) => state.user)
  const canManage = forType === "group" && util.hasPermission(user, "org.manageGroups", groupQuery.data?.organisation)
  const canPostNewComments = util.hasPermission(user, "discussion.postNewComments", discussionId, forId)
  const canPostMedia = util.hasPermission(user, "discussion.postMedia", discussionId, forId)

  const pinnedComment = discussionCommentQuery.data?.comments?.find((c) => c.isPinned)

  const postComment = useCallback(
    (parentCommentId = "new") => {
      const commentData = commentState[parentCommentId] || { message: "", media: [] }
      const data: {
        message: string
        media: Array<{
          value: string
          name: string
          signedDownloadRequest?: string
          type: "image" | "file" | "gif"
          data?: any
        }>
        parent?: string
      } = {
        message: commentData.message,
        media: commentData.media,
      }
      if (parentCommentId !== "new") {
        data.parent = parentCommentId
      }

      setCommentState((prevState) => ({
        ...prevState,
        [parentCommentId]: { ...prevState[parentCommentId], loading: true },
      }))

      api.discussions.addComment(
        discussionId,
        data,
        () => {
          api.queryClient.invalidateQueries({
            queryKey: ["discussions", discussionId, "comments"],
          })

          setCommentState((prevState) => {
            const prevComment = prevState[parentCommentId] || { message: "", media: [] }
            return {
              ...prevState,
              [parentCommentId]: {
                ...prevComment,
                message: "",
                media: [],
                loading: false,
              },
            }
          })
        },
        (err) => {
          toast.error(err.message)
          setCommentState((prevState) => ({
            ...prevState,
            [parentCommentId]: { ...(prevState[parentCommentId] || { message: "", media: [] }), loading: false },
          }))
        },
      )
    },
    [discussionId, commentState],
  )

  const approveComment = useCallback(
    (commentId) => {
      api.discussions.approveComment(
        discussionId,
        commentId,
        () => {
          api.queryClient.invalidateQueries({
            queryKey: ["discussions", discussionId, "comments"],
          })
        },
        (err) => toast.error(err.message),
      )
    },
    [discussionId],
  )

  const rejectComment = useCallback(
    (commentId) => {
      api.discussions.rejectComment(
        discussionId,
        commentId,
        () => {
          api.queryClient.invalidateQueries({
            queryKey: ["discussions", discussionId, "comments"],
          })
        },
        (err) => toast.error(err.message),
      )
    },
    [discussionId],
  )

  const deleteComment = useCallback(
    (commentId) =>
      util
        .confirm(t("feeds.comments.delete.title"), t("feeds.comments.delete.info"))
        .then(() => {
          api.discussions.deleteComment(
            discussionId,
            commentId,
            () => {
              api.queryClient.invalidateQueries({
                queryKey: ["discussions", discussionId, "comments"],
              })
              toast(t("generic.typeDeleted", { type: "Comment" }))
            },
            (err) => toast.error(err.message),
          )
        })
        .catch(() => {}),
    [t, discussionId],
  )

  const pinComment = useCallback(
    (commentId) => {
      api.discussions.pinComment(
        discussionId,
        commentId,
        true,
        () => {
          api.queryClient.invalidateQueries({
            queryKey: ["discussions", discussionId, "comments"],
          })
        },
        () => {},
      )
    },
    [discussionId],
  )

  const unpinComment = useCallback(
    (commentId) => {
      api.discussions.pinComment(
        discussionId,
        commentId,
        false,
        () => {
          api.queryClient.invalidateQueries({
            queryKey: ["discussions", discussionId, "comments"],
          })
        },
        () => {},
      )
    },
    [discussionId],
  )

  const reactComment = useCallback(
    (commentId, emoji, adding) => {
      const commentReactionUpdater = adding ? api.discussions.commentAddReaction : api.discussions.commentRemoveReaction
      commentReactionUpdater(
        discussionId,
        commentId,
        emoji,
        (data) => {
          api.queryClient.invalidateQueries({
            queryKey: ["discussions", discussionId, "comments"],
          })
        },
        () => {},
      )
    },
    [discussionId],
  )

  const updateComment = useCallback(
    (parentCommentId = "new", commentOrUpdater: CommentState["string"] | SetStateAction<CommentState["string"]>) => {
      setCommentState((prevState) => {
        if (typeof commentOrUpdater === "function") {
          const update = commentOrUpdater(prevState[parentCommentId] || { message: "", media: [] })
          return {
            ...prevState,
            [parentCommentId]: update,
          }
        } else {
          const prevComment = prevState[parentCommentId] || { message: "", media: [] }
          return {
            ...prevState,
            [parentCommentId]: {
              ...prevComment,
              ...commentOrUpdater,
            },
          }
        }
      })
    },
    [],
  )

  const onPageChange = useCallback((newPage) => {
    setParams((prevParams) => ({
      ...prevParams,
      page: newPage,
    }))
  }, [])

  if (!discussionId) {
    return (
      <Message info>
        <Message.Header>{t("challenge.discussion.noDiscussionSelectedTitle")}</Message.Header>
        <p>{t("challenge.discussion.noDiscussionSelectedText")}</p>
      </Message>
    )
  }

  if (forType === "group" && !location.pathname.startsWith("/groups/")) {
    return <Navigate replace to={`/groups/${forId}/discussions/${discussionId}`} />
  }

  if (discussionCommentQuery.isLoading) {
    return <Loader active inline="centered" />
  }

  if (discussionCommentQuery.isError) {
    return (
      <Message negative>
        <Message.Header>{t("challenge.discussion.errorLoadingTitle")}</Message.Header>
        <p>{t("challenge.discussion.errorLoadingText")}</p>
      </Message>
    )
  }

  return (
    <div>
      <Header>{discussionCommentQuery.data?.discussion?.title}</Header>
      {discussionCommentQuery.data?.discussion?.description ? (
        <RichTextViewer value={discussionCommentQuery.data?.discussion?.description || ""} />
      ) : null}

      {forType === "group" ? (
        user?.eventSubscriptions?.groupDiscussions?.includes("email") ? (
          <Message
            success
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 10,
              marginTop: 0,
            }}
          >
            <div>
              <Icon name="bell" /> {t("groups.discussion.notifications")}
            </div>
            <Button basic compact size="tiny" as={Link} to="/preferences/notifications#subscription-groups">
              {t("admin.settings.signOn.signOnMethods.manage")}
            </Button>
          </Message>
        ) : (
          <Message error style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <Icon name="bell slash" /> {t("discussion.alerts.off")}
            </div>
            <Button basic compact size="tiny" as={Link} to="/preferences/notifications#subscription-groups">
              {t("notifications.update")}
            </Button>
          </Message>
        )
      ) : null}

      {!pinnedComment && canManage ? (
        <Message
          size="tiny"
          info
          style={{
            display: "inline-block",
            width: "100%",
            marginBottom: 10,
            marginTop: 0,
          }}
        >
          <Message.Header>
            <div>
              <Icon name="pin" />
              <span style={{ marginLeft: "2px" }}> Why not pin a post?</span>
            </div>
          </Message.Header>
          <p style={{ marginLeft: 25, marginBottom: 0 }}>
            Pinning a post can be useful to explain the purpose of the discussion and helps to drive user engagement. To
            pin a post, find an existing entry in the feed below and tap "Pin comment".
          </p>
        </Message>
      ) : null}
      {canPostNewComments || canManage ? (
        <CommentInput
          inputRef={inputRef}
          forType={forType}
          forId={forId}
          discussionId={discussionId}
          currentReply={commentState.new}
          updateReply={(commentOrUpdater) => updateComment("new", commentOrUpdater)}
          postComment={postComment}
          canPostMedia={canPostMedia || canManage}
        />
      ) : null}

      <Comment.Group style={{ display: "flex", flexDirection: "column", maxWidth: "100%" }}>
        {(discussionCommentQuery.data?.comments || []).map((item) => (
          <DisscussionComment
            key={item._id}
            forType={forType}
            forId={forId}
            discussionId={discussionId}
            comment={item}
            replies={discussionCommentQuery.data?.replies?.[item._id] || []}
            currentReply={commentState[item._id] || { message: "", media: [] }}
            canManage={canManage}
            updateReply={updateComment}
            postComment={() => postComment(item._id)}
            deleteComment={deleteComment}
            approveComment={approveComment}
            moderationEnabled={discussionCommentQuery.data?.discussion?.discussionModeration || false}
            rejectComment={rejectComment}
            pinComment={pinComment}
            unpinComment={unpinComment}
            reactComment={reactComment}
          />
        ))}
      </Comment.Group>
      {discussionCommentQuery.data?.previousPageAvailable || discussionCommentQuery.data?.nextPageAvailable ? (
        <Pagination
          onPageChange={(_e, { activePage }) => onPageChange(activePage as number)}
          activePage={discussionCommentQuery.data?.page}
          totalPages={Math.ceil(discussionCommentQuery.data?.total / 20)}
          boundaryRange={1}
          siblingRange={1}
          ellipsisItem={null}
          firstItem={null}
          lastItem={null}
        />
      ) : null}
    </div>
  )
}
